import * as xlsx from 'xlsx';
import moment from 'moment-timezone';
import i18n from 'i18next';
import {matchesAnyLocalizedField} from 'helpers/localizedField';

/** 템플릿종류 값 ↔ 번역 키. 엑셀 템플릿종류 칸 값을 언어와 관계없이 코드로 바꿀 때 쓴다 */
const TEMPLATE_TYPE_KEYS = [
    { value: 'WORK_SCHDULE', key: 'shiftManagement.work', permission: 'work' },
    { value: 'REST_SCHDULE', key: 'shiftManagement.vacation', permission: 'vacation' },
    { value: 'REDUCE_SCHDULE', key: 'shiftManagement.reduce', permission: 'reduce' },
];

/** 엑셀에서 받을 수 있는 언어. 업로드 모달이 열릴 때 이 언어들의 shiftManagement 번역을 불러온다 */
export const EXCEL_UPLOAD_LANGS = ['ko', 'en', 'vi', 'ch'];

/**
 * 엑셀 템플릿종류 칸 값(어느 언어든)을 WORK_SCHDULE 등으로 바꾼다. 권한이 없는 종류나 모르는 값은 null
 * @param {string} val       엑셀 칸 값
 * @param {Object} templete  user.me.permissions.detailRole.templete
 */
export const resolveTemplateType = (val, templete = {}) => {
    const target = String(val ?? '').trim();
    if (!target) return null;
    const found = TEMPLATE_TYPE_KEYS.find(({ key }) =>
        EXCEL_UPLOAD_LANGS.some((lng) => i18n.getFixedT(lng)(key) === target),
    );
    return found && templete?.[found.permission] ? found.value : null;
};

/** 목록(근무그룹·휴가종류)에서 엑셀 칸 값과 표시 이름이나 어느 언어 이름이든 같은 항목을 찾는다 */
export const findByAnyName = (list = [], val) => {
    const target = String(val ?? '').trim();
    if (!target) return undefined;
    return list.find((item) => String(item?.label ?? '').trim() === target || matchesAnyLocalizedField(item, target));
};

/**
 * 샘플 엑셀의 예시 행(key1 → 표시 값). 템플릿종류·근무그룹·휴가종류는 현재 언어 이름으로 넣는다
 * @param {Function} t
 * @param {string} tab  'templates' | 'pattern' | 'personnel'
 * @param {Object} lists { workGroupLists, restTypeLists }
 */
export const getSampleRows = (t, tab, { workGroupLists = [], restTypeLists = [] } = {}) => {
    const groupLabel = workGroupLists[0]?.label ?? '';
    const restLabel = restTypeLists.find((r) => r.value)?.label ?? '';
    const startDt = moment().startOf('month');

    if (tab === 'templates') {
        const restTime = {};
        for (let i = 0; i < 7; i++) {
            restTime[`restTime[${i}][0]`] = '12:00';
            restTime[`restTime[${i}][1]`] = '13:00';
        }
        return [
            {
                name: 'Shift A', parentGroupList: groupLabel, type: t('shiftManagement.work'), code: '15 TO 23',
                shortcutKey: 3, color: 'FF6900', 'workTime[0][0]': '15:00', 'workTime[0][1]': '23:00', restAuto: 'N',
                ...restTime,
            },
            {
                name: 'Shift A (AM)', parentGroupList: groupLabel, type: t('shiftManagement.vacation'), code: '15 TO 19',
                shortcutKey: 5, color: '00D084', 'workTime[0][0]': '15:00', 'workTime[0][1]': '19:00', parentId: restLabel,
            },
        ];
    }

    if (tab === 'pattern') {
        const cycle = {};
        for (let i = 0; i < 14; i++) {
            cycle[`patternCycleList[${i}]`] = i < 7 ? '15 TO 23' : '07 TO 15';
        }
        return [{
            patternCycleName: 'Line A Team', patternCycleCode: 'CM-A', parentGroupList: groupLabel,
            cycleStartDt: startDt.format('MM/DD/YYYY'), cycleTotalDays: 14, ...cycle,
        }];
    }

    if (tab === 'personnel') {
        return [{
            patternCycleCode: 'CM-A', companyNumber: '325011',
            effectStartDt: startDt.format('MM/DD/YYYY'), effectEndDt: startDt.clone().add(8, 'months').endOf('month').format('MM/DD/YYYY'),
        }];
    }

    return [];
};

/**
 * Tab configuration for Shift Management
 * @param {Function} t - Translation function
 * @returns {Array} - Array of tab objects
 */
export const getTabsConfig = (t) => [
    {
        key: 'templates',
        label: t('shiftManagement.templates'),
        name: 'templates',
    },
    {
        key: 'pattern',
        label: t('shiftManagement.schedulePattern'),
        name: 'schedulePattern',
    },
    {
        key: 'personnel',
        label: t('shiftManagement.personnelAssignment'),
        name: 'personnelAssignment',
    },
];

export const getTabsDownloadNm = (t) => [
    {
        key: 'templates',
        label: t('shiftManagement.templateSample'),
        name: 'templates',
    },
    {
        key: 'pattern',
        label: t('shiftManagement.patternSample'),
        name: 'schedulePattern',
    },
    {
        key: 'personnel',
        label: t('shiftManagement.personnelSample'),
        name: 'personnelAssignment',
    },
];

export const ALL_OPTION = 'ALL';
export const MAX_CHAR_MEMO = 255;
export const MAX_CHAR_CODE = 50;
export const MAX_CHAR_NAME = 100;
export const MAX_TOTAL_DAYS = 62;

export const templateTypeOptions = (t, templete = {}) => [
    { value: ALL_OPTION, label: t('common.label.all') },

    ...(templete?.work
        ? [{ value: 'WORK_SCHDULE', label: t('shiftManagement.work') }]
        : []),

    ...(templete?.vacation
        ? [{ value: 'REST_SCHDULE', label: t('shiftManagement.vacation') }]
        : []),

    ...(templete?.reduce
        ? [{ value: 'REDUCE_SCHDULE', label: t('shiftManagement.reduce') }]
        : []),
];

/**
 * Column data configurations by tab
 * Usage: getColumnsList(t)('templates' | 'personnel' | 'pattern')
 */
export const getColumnsList = (t) => (tabName) => {
    const byTab = {
        templates: [
            { grow: 1, width: 200, name: '', key1: 'templateItem' },
            { grow: 1, width: 200, name: t('shiftManagement.templateName'), key1: 'name' },
            { grow: 1, width: 150, name: t('shiftManagement.workGroup'), key1: 'parentGroupList' },
            { grow: 1, width: 150, name: t('shiftManagement.templateType'), key1: 'type' },
            { grow: 1, width: 120, name: t('shiftManagement.code'), key1: 'code' },
            { grow: 1, width: 150, name: t('shiftManagement.shortcutKeys'), key1: 'shortcutKey' },
            { grow: 1, width: 120, name: t('shiftManagement.colour'), key1: 'color' },
            { grow: 1, width: 150, name: t('shiftManagement.time'), key1: 'workTime' },
            { grow: 1, width: 180, name: t('shiftManagement.settingRestTime'), key1: 'restTime' },
            { grow: 1, width: 180, name: t('shiftManagement.displayLeaveType'), key1: 'parentId' },
            { grow: 1, width: 150, name: t('workType.shortTimeWorkKind'), key1: 'reduceTypeList' },
        ],
        personnel: [
            { grow: 1, width: 200, name: t('shiftManagement.name'), key1: 'userInfo' },
            { grow: 1, width: 150, name: t('shiftManagement.empNo'), key1: 'companyNumber' },
            { grow: 1, width: 200, name: t('shiftManagement.email'), key1: 'email' },
            { grow: 1, width: 200, name: t('shiftManagement.department'), key1: 'dptName' },
            { grow: 1, width: 150, name: t('shiftManagement.workGroup'), key1: 'groupName' },
            { grow: 1, width: 180, name: t('shiftManagement.schedulePattern'), key1: 'patternCycleCode' },
            {
                grow: 1,
                width: 180,
                name: t('shiftManagement.applicationStartDate'),
                key1: 'effectStartDt',
            },
            { grow: 1, width: 180, name: t('shiftManagement.applicationEndDate'), key1: 'effectEndDt' },
        ],
        pattern: [
            {
                grow: 1,
                width: 220,
                name: t('shiftManagement.schedulePatternName'),
                key1: 'patternCycleName',
            },
            { grow: 1, width: 150, name: t('shiftManagement.workGroup'), key1: 'parentGroupList' },
            { grow: 1, width: 180, name: t('shiftManagement.patternStartDate'), key1: 'cycleStartDt' },
            { grow: 1, width: 120, name: t('shiftManagement.totalDays'), key1: 'cycleTotalDays' },
            { grow: 1, width: 300, name: t('shiftManagement.memo'), key1: 'memo' },
            {
                grow: 1,
                width: 180,
                name: t('shiftManagement.schedulePatternCode'),
                key1: 'patternCycleCode',
            },
        ],
    };
    return byTab[tabName] || [];
};

export const getExcelColumnsList = (t, lang) => (tabName) => {
    const byTab = {
        templates: [
            { grow: 1, width: 200, name: t('shiftManagement.templateName'), key1: 'name' },
            { grow: 1, width: 250, name: t('shiftManagement.workGroup'), key1: 'parentGroupList' },
            { grow: 1, width: 150, name: t('shiftManagement.templateType'), key1: 'type' },
            { grow: 1, width: 120, name: t('shiftManagement.code'), key1: 'code' },
            { grow: 1, width: 150, name: t('shiftManagement.shortcutKeys'), key1: 'shortcutKey' },
            { grow: 1, width: 120, name: t('shiftManagement.colour'), key1: 'color' },
            { grow: 1, width: 120, name: t('shiftManagement.memo'), key1: 'memo' },
            { grow: 1, width: 60, name: t('common.label.startTime'), key1: 'workTime[0][0]' },
            { grow: 1, width: 60, name: t('common.label.endTime'), key1: 'workTime[0][1]' },
            { grow: 1, width: 180, name: t('shiftManagement.displayLeaveType'), key1: 'parentId' },
            { grow: 1, width: 250, name: t('workType.shortTimeWorkKind'), key1: 'reduceTypeList' },
            { grow: 1, width: 180, name: t('shiftManagement.settingRestTime'), key1: 'restAuto' },

            { grow: 1, width: 80, name: t('shiftManagement.monStart'), key1: 'restTime[0][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.monEnd'), key1: 'restTime[0][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.tueStart'), key1: 'restTime[1][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.tueEnd'), key1: 'restTime[1][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.wedStart'), key1: 'restTime[2][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.wedEnd'), key1: 'restTime[2][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.thuStart'), key1: 'restTime[3][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.thuEnd'), key1: 'restTime[3][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.friStart'), key1: 'restTime[4][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.friEnd'), key1: 'restTime[4][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.satStart'), key1: 'restTime[5][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.satEnd'), key1: 'restTime[5][1]' },
            { grow: 1, width: 80, name: t('shiftManagement.sunStart'), key1: 'restTime[6][0]' },
            { grow: 1, width: 80, name: t('shiftManagement.sunEnd'), key1: 'restTime[6][1]' },
        ],
        personnel: [
            { grow: 1, width: 180, name: t('shiftManagement.schedulePattern'), key1: 'patternCycleCode' },
            {
                grow: 1,
                width: 150,
                name: t('shiftManagement.schedulePatternName'),
                key1: 'patternNameShowOnly',
            },
            { grow: 1, width: 150, name: t('shiftManagement.empNo'), key1: 'companyNumber' },
            {
                grow: 1,
                width: 180,
                name: t('shiftManagement.applicationStartDate'),
                key1: 'effectStartDt',
            },
            { grow: 1, width: 180, name: t('shiftManagement.applicationEndDate'), key1: 'effectEndDt' },
        ],
        pattern: [
            {
                grow: 1,
                width: 220,
                name: t('shiftManagement.schedulePatternName'),
                key1: 'patternCycleName',
            },
            {
                grow: 1,
                width: 140,
                name: t('shiftManagement.schedulePatternCode'),
                key1: 'patternCycleCode',
            },
            { grow: 1, width: 120, name: t('shiftManagement.workGroup'), key1: 'parentGroupList' },
            { grow: 1, width: 180, name: t('shiftManagement.patternStartDate'), key1: 'cycleStartDt' },
            { grow: 1, width: 60, name: t('shiftManagement.totalDays'), key1: 'cycleTotalDays' },
            { grow: 1, width: 300, name: t('shiftManagement.memo'), key1: 'memo' },
            ...createPatternCycleList(lang),
        ],
    };
    return byTab[tabName] || [];
};

export const getDayLabelText = (day, lang = 'ko') => {
    const normalizedLang = (lang || 'ko').split('-')[0];

    const suffix = (n) => {
        if (n % 100 >= 11 && n % 100 <= 13) return 'th';
        const mod = n % 10;
        if (mod === 1) return 'st';
        if (mod === 2) return 'nd';
        if (mod === 3) return 'rd';
        return 'th';
    };

    if (normalizedLang === 'ko') return `${day}일`;
    if (normalizedLang === 'vi') return `Ngày ${day}`;
    if (normalizedLang === 'ch') return `第${day}天`;
    return `${day}${suffix(day)}`;
};

const createPatternCycleList = (lang = 'ko') => {
    const list = [];

    for (let i = 0; i < MAX_TOTAL_DAYS; i++) {
        list.push({
            grow: 1,
            width: 70,
            name: getDayLabelText(i + 1, lang),
            key1: `patternCycleList[${i}]`,
        });
    }
    return list;
};

// Convert "HH:MM" -> minutes
function timeToMinutes(t) {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
}

function minutesToTime(mins) {
    mins = mins % (24 * 60);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

function handleStartEnd(start, end, opts = {}) {
    const defaultShift = opts.defaultShift || 9 * 60; // 9 hours
    const fixedEnd = opts.fixedEnd || null;

    let s = start ? timeToMinutes(start) : null;
    let e = end ? timeToMinutes(end) : null;

    // ----------------------------------------------------
    // 1) CHOOSE START TIME → AUTO CALCULATE END TIME
    // ----------------------------------------------------
    if (s !== null && opts.trigger === 'start') {
        if (fixedEnd) {
            e = timeToMinutes(fixedEnd);
        } else {
            e = s + defaultShift;
        }

        const nextDay = e >= 24 * 60;

        if (nextDay) {
            e = e - 24 * 60;
        }

        return {
            start: minutesToTime(s),
            end: minutesToTime(e),
            nextDay,
        };
    }

    return {
        start: minutesToTime(s),
        end: minutesToTime(e),
        nextDay: false,
    };
}

/**
 * Configuration for dynamic search fields based on selected tab
 * @param {Function} t - Translation function
 * @returns {Object} - Search fields configuration
 */
export const getSearchFieldsConfig = (t, templete = {}, optionList) => {
    const { workGroupList = [], patternCycleList = [],  reduceTypeList = []} = optionList;
    // Tab-specific fields - object with field names as keys
    return {
        // Templates tab specific fields
        name: {
            name: 'name',
            type: 'input',
            placeholder: t('shiftManagement.enterTemplateName'),
            onChange: 'setTemplateName',
            fieldName: 'templateNameInput',
        },
        groupIds: {
            name: 'groupIds',
            type: 'select',
            placeholder: t('shiftManagement.selectWorkGroup'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...workGroupList],
            onChange: 'setWorkGroup',
            fieldName: 'workGroupSelect',
            valueShouldBeArray: true,
        },
        typeArray: {
            name: 'typeArray',
            type: 'select',
            placeholder: t('shiftManagement.selectTemplateType'),
            options: () => templateTypeOptions(t, templete),
            onChange: 'setTemplateType',
            fieldName: 'templateTypeSelect',
            valueShouldBeArray: true,
        },
        startTime: {
            name: 'startTime',
            type: 'timeCustom',
            placeholder: t('shiftManagement.startTime'),
            onChange: (value, fm, shiftManagement) => {
                const endTime = fm.values['endTime'];
                const result = handleStartEnd(value, endTime, { trigger: 'start' });
                fm.setFieldValue('startTime', result.start);
                fm.setFieldValue('endTime', result.end);
                fm.setFieldValue('nextDay', result.nextDay);
                shiftManagement.setFilterParams({ startTime: result.start });
                shiftManagement.setFilterParams({ endTime: result.end });
                fm.handleSubmit();
            },
            fieldName: 'startTimeInput',
        },
        endTime: {
            name: 'endTime',
            type: 'timeCustom',
            placeholder: t('shiftManagement.endTime'),
            onChange: (value, fm, shiftManagement) => {
                const startTime = fm.values['startTime'];
                const result = handleStartEnd(startTime, value, { trigger: 'end' });
                fm.setFieldValue('startTime', result.start);
                fm.setFieldValue('endTime', result.end);
                fm.setFieldValue('nextDay', result.nextDay);
                shiftManagement.setFilterParams({ startTime: result.start });
                shiftManagement.setFilterParams({ endTime: result.end });

                fm.handleSubmit();
            },
            fieldName: 'endTimeInput',
        },

        // Personnel Assignment tab specific fields
        userName: {
            name: 'userName',
            type: 'input',
            placeholder: t('shiftManagement.enterPersonalInfo'),
            onChange: 'setName',
            fieldName: 'nameInput',
        },
        patternCycleId: {
            name: 'patternCycleId',
            type: 'select',
            placeholder: t('shiftManagement.selectSchedulePattern'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...patternCycleList],
            onChange: 'setSchedulePattern',
            fieldName: 'schedulePatternSelect',
        },
        groupId: {
            name: 'groupId',
            type: 'select',
            placeholder: t('shiftManagement.selectWorkGroup'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...workGroupList],
            onChange: 'setWorkGroup',
            fieldName: 'parentGroupListSelect',
        },

        // Schedule Pattern tab specific fields
        patternCycleName: {
            name: 'patternCycleName',
            type: 'input',
            placeholder: t('shiftManagement.enterSchedulePatternName'),
            onChange: 'setPatternName',
            fieldName: 'patternNameInput',
        },
        parentGroupList: {
            name: 'parentGroupList',
            type: 'select',
            placeholder: t('shiftManagement.selectWorkGroup'),
            options: () => [{ value: ALL_OPTION, label: t('common.label.all') }, ...workGroupList],
            onChange: 'setWorkGroup',
            fieldName: 'parentGroupListSelect',
            valueShouldBeArray: true,
        },
    };
};

export const readExcelFile = (file, columns) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = xlsx.read(data, { type: 'binary' });
                const sheetCellData = workbook.Sheets[workbook.SheetNames[0]];
                const range = xlsx.utils.decode_range(sheetCellData['!ref']);
                // First row is notation, so data starts from row 2
                const startRow = 2, startCol = 0;
                const endRow = range.e.r + 1, endCol = range.e.c;
                const dynamicRange = `${xlsx.utils.encode_col(startCol)}${startRow}:${xlsx.utils.encode_col(
                    endCol,
                )}${endRow}`;

                const sheetData = xlsx.utils.sheet_to_json(sheetCellData, {
                    range: dynamicRange,
                    header: 1,
                    defval: '',
                    raw: true,
                });

                // Remove header row
                if (sheetData.length > 0) {
                    sheetData.shift();
                }

                // Filter rows that have at least one value
                const filteredData = sheetData.filter((row) =>
                    row.some((val) => val !== null && val !== undefined && String(val).trim() !== ''),
                );

                // Helper: robust Excel date formatter to MM/DD/YYYY
                const formatExcelDate = (val) => {
                    try {
                        // Excel serial number (number) or numeric string
                        if (typeof val === 'number' || (typeof val === 'string' && /^\d+(\.\d+)?$/.test(val))) {
                            const num = typeof val === 'number' ? val : parseFloat(val);
                            const dc = xlsx.SSF.parse_date_code(num);
                            if (dc && dc.y && dc.m && dc.d) {
                                const mm = String(dc.m).padStart(2, '0');
                                const dd = String(dc.d).padStart(2, '0');
                                const yyyy = String(dc.y);
                                return `${mm}/${dd}/${yyyy}`;
                            }
                        }

                        // Special handling: two-digit year like MM/DD/YY
                        const twoDigitMatch = typeof val === 'string' && val.match(/^([0-1]?\d)\/([0-3]?\d)\/(\d{2})$/);
                        if (twoDigitMatch) {
                            const mm = twoDigitMatch[1].padStart(2, '0');
                            const dd = twoDigitMatch[2].padStart(2, '0');
                            const yy = parseInt(twoDigitMatch[3], 10);
                            const candidate2000 = 2000 + yy;
                            const candidate1900 = 1900 + yy;
                            const today = new Date();
                            const futureThreshold = new Date(today.getFullYear() + 1, 0, 1); // slight buffer
                            const as2000 = new Date(candidate2000, parseInt(mm, 10) - 1, parseInt(dd, 10));
                            // Prefer 1900s if the 2000s candidate would land in the future
                            const fullYear = as2000 > futureThreshold ? candidate1900 : candidate2000;
                            return `${mm}/${dd}/${fullYear}`;
                        }

                        // Try multiple common string formats
                        const m = moment(
                            val,
                            ['MM/DD/YYYY', 'MM/DD/YY', 'YYYY.MM.DD', 'YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD'],
                            true,
                        );
                        if (m.isValid()) {
                            return m.format('MM/DD/YYYY');
                        }

                        // Fallback: native Date parsing if it succeeds
                        const d = new Date(val);
                        if (!isNaN(d.getTime())) {
                            const month = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            const year = d.getFullYear();
                            return `${month}/${day}/${year}`;
                        }
                    } catch (e) {
                        // ignore and fall through
                    }
                    return val; // return original if unable to format
                };

                // Map Excel data to Grid columns by order
                const mappedData = filteredData.map((row) => {
                    const newRow = {};
                    columns
                        .filter((col) => col.key1 !== 'patternNameShowOnly')
                        .forEach((col, index) => {
                            if (row[index] !== undefined) {
                                let value = String(row[index]);

                                // Format date for cycleStartDt using robust formatter
                                if (['cycleStartDt', 'effectStartDt', 'effectEndDt'].includes(col.key1) && value) {
                                    value = formatExcelDate(value);
                                }

                                newRow[col.key1] = value;
                            } else {
                                newRow[col.key1] = '';
                            }
                        });
                    return newRow;
                });

                resolve(mappedData);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = (error) => reject(error);
        reader.readAsBinaryString(file);
    });
};

export const validateExcelData = ({
  data,
  columns,
  t,
  workGroupLists,
  restTypeLists,
  templete,
  templateList,
  patternCycleList,
  employList,
  tab,
}) => {
  const errors = {};
  let hasInvalidRows = false;

  const newData = data.map((row) => {
    const newRow = { ...row };
    let rowIsValid = true;
    const isRequiredVacationType = resolveTemplateType(row.type, templete) === 'REST_SCHDULE';

    const addError = (colName, msg) => {
      errors[colName] = msg;
      hasInvalidRows = true;
    };

    const markInvalid = (key, value) => {
      if (!String(value).startsWith('#')) {
        newRow[key] = `#${value}#`;
      }
      rowIsValid = false;
    };

    const markValid = (key, value) => {
      if (String(value).startsWith('#') && String(value).endsWith('#')) {
        newRow[key] = value.substring(1, value.length - 1);
      }
    };

    const check = (key1, validator, errorMsg, isRequired = false, requiredMsg) => {
      const column = columns.find((c) => c.key1 === key1);
      if (column) {
        const key = column.key1;
        const displayKey = column.name;
        let value = newRow[key];

        // Remove # if present for validation
        if (typeof value === 'string' && value.startsWith('#') && value.endsWith('#')) {
          value = value.substring(1, value.length - 1);
        }

        const isEmpty = value === null || value === undefined || String(value).trim() === '';

        if (isRequired && isEmpty) {
          markInvalid(key, value || '');
          const reqMsg = requiredMsg
            ? requiredMsg
            : t('shiftManagement.validation.requiredField', { name: displayKey });
          addError(displayKey, reqMsg);
          return;
        }

        if (!isEmpty && !validator(value)) {
          markInvalid(key, value);
          addError(displayKey, errorMsg);
        } else {
          markValid(key, newRow[key]);
        }
      }
    };

    // Work Group is required and must be in workGroupLists
    check(
      'parentGroupList',
      (val) => {
        return !!findByAnyName(workGroupLists, val);
      },
      t('shiftManagement.validation.pleaseCheckField', { name: t('shiftManagement.workGroup') }),
      tab === 'pattern',
    );

    if (tab === 'templates') {
      // 1. Template Name is required
      check('name', () => true, '', true);

      // 3. Template Type: 근무/휴가/단축근로 중 권한 있는 종류(어느 언어로 적어도 됨)
      check(
        'type',
        (val) => !!resolveTemplateType(val, templete),
        t('shiftManagement.validation.invalidType'),
      );

      // 4. Code: required
      check('code', () => true, '', true);

      // 5. Shortcut Key: 2 to 9
      check(
        'shortcutKey',
        (val) => {
          const num = Number(val);
          return !isNaN(num) && num >= 2 && num <= 9;
        },
        t('shiftManagement.validation.range2to9'),
      );

      // 6. Colour: Hex code
      check(
        'color',
        (val) => {
          return /^[0-9A-F]{6}$/i.test(val);
        },
        t('shiftManagement.validation.invalidHexColor'),
      );

      // 7. Start Time, End Time: Time format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      check(
        'startTime',
        (val) => timeRegex.test(val),
        t('shiftManagement.validation.invalidTimeFormat'),
      );
      check(
        'endTime',
        (val) => timeRegex.test(val),
        t('shiftManagement.validation.invalidTimeFormat'),
      );
      check(
        'workTime[0][0]',
        (val) => timeRegex.test(val),
        t('shiftManagement.validation.invalidTimeFormat'),
        true,
      );
      check(
        'workTime[0][1]',
        (val) => timeRegex.test(val),
        t('shiftManagement.validation.invalidTimeFormat'),
        true,
      );

      // 8. Vacation Type: in restTypeLists
      check(
        'parentId',
        (val) => {
          return isRequiredVacationType
            ? !!findByAnyName(restTypeLists, val)
            : true;
        },
        t('shiftManagement.validation.invalidVacationType'),
        isRequiredVacationType,
      );

      // 9. Breaktime Time Type: Y or N
      check(
        'restAuto',
        (val) => {
          return ['Y', 'N'].includes(String(val).toUpperCase());
        },
        t('shiftManagement.validation.mustBeYorN'),
      );

      // 10. Days of week
      for (let i = 0; i < 7; i++) {
        for (let j = 0; j < 2; j++) {
          check(
            `restTime[${i}][${j}]`,
            (val) => timeRegex.test(val),
            t('shiftManagement.validation.invalidTimeFormat'),
          );
        }
      }
    }

    if (tab === 'pattern') {
      check(
        'patternCycleName',
        (val) => String(val).length <= MAX_CHAR_NAME,
        t('shiftManagement.validation.pleaseCheckField', {
          name: t('shiftManagement.schedulePatternName'),
        }),
        true,
      );

      check(
        'cycleStartDt',
        (val) => {
          if (!val || String(val).trim() === '') return false;
          // Validate MM/DD/YYYY format
          const parsed = moment(val, 'MM/DD/YYYY', true);
          return parsed.isValid();
        },
        t('shiftManagement.validation.invalidDateFormat'),
        true,
      );

      check(
        'cycleTotalDays',
        (val) => {
          const num = Number(val);
          return !isNaN(num) && num >= 0 && num <= MAX_TOTAL_DAYS;
        },
        t('shiftManagement.validation.pleaseCheckField', { name: t('shiftManagement.totalDays') }),
        true,
      );

      check(
        'memo',
        (val) => String(val).length <= MAX_CHAR_MEMO,
        t('shiftManagement.validation.pleaseCheckField', { name: t('shiftManagement.memo') }),
      );

      check(
        'patternCycleCode',
        (val) => String(val).trim().length <= MAX_CHAR_CODE,
        t('shiftManagement.validation.pleaseCheckField', {
          name: t('shiftManagement.schedulePatternCode'),
        }),
        true,
      );

      for (let i = 0; i < row.cycleTotalDays; i++) {
        check(
          `patternCycleList[${i}]`,
          (val) => templateList?.some((t) => String(t.code.trim()) === String(val.trim())),
          t('shiftManagement.validation.pleaseCheckField', {
            name: t('shiftManagement.patternCycleList'),
          }),
        );
      }
    }

    if (tab === 'personnel') {
      // 1. companyNumber is required and must belong to employList
      check(
        'companyNumber',
        (val) => {
          return employList?.some((emp) => String(emp.companyNumber) === String(val));
        },
        t('shiftManagement.validation.pleaseCheckField', { name: t('shiftManagement.empNo') }),
        true,
      );

      // 2. patternCycleCode is required and must belong to patternCycleList
      check(
        'patternCycleCode',
        (val) => {
          return patternCycleList?.some((pattern) => String(pattern.code) === String(val));
        },
        t('shiftManagement.validation.pleaseCheckField', {
          name: t('shiftManagement.schedulePattern'),
        }),
        true,
      );

      // 3. effectStartDt is required and must be in MM/DD/YYYY format
      check(
        'effectStartDt',
        (val) => {
          if (!val || String(val).trim() === '') return false;
          const parsed = moment(val, 'MM/DD/YYYY', true);
          return parsed.isValid();
        },
        t('shiftManagement.validation.invalidDateFormat'),
        true,
      );

      // 4. must be in MM/DD/YYYY format and after effectStartDt
      check(
        'effectEndDt',
        (val) => {
          if (!val || String(val).trim() === '') return false;
          const parsed = moment(val, 'MM/DD/YYYY', true);

          if (parsed.isValid()) {
            if (parsed.isBefore(moment(row.effectStartDt, 'MM/DD/YYYY', true), 'day')) {
              return false;
            }
            return true;
          }

          return false;
        },
        moment(row.effectEndDt, 'MM/DD/YYYY', true).isBefore(
          moment(row.effectStartDt, 'MM/DD/YYYY', true),
          'day',
        )
          ? t('shiftManagement.validation.invalidEndDate')
          : t('shiftManagement.validation.invalidDateFormat'),
      );
    }

    return newRow;
  });

  return { newData, errors, hasInvalidRows };
};

// Format time to ensure HH:MM format with leading zeros
const formatTimeWithLeadingZero = (timeStr) => {
  if (!timeStr) return '';
  const parts = String(timeStr).split(':');
  if (parts.length !== 2) return timeStr;
  const hours = String(parts[0]).padStart(2, '0');
  const minutes = String(parts[1]).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Transform Excel data to create template payload format
 * @param {Array} excelData - Array of Excel row data
 * @param {Object} options - Options including workGroupLists, restTypeLists, companyId, t
 * @param {Object} initPayload - Initial payload template
 * @returns {Array} - Transformed payload data
 */
export const transformExcelToTemplatePayload = (excelData, options = {}, initPayload = {}, templete = {}) => {
    const { workGroupLists = [], restTypeLists = [], companyId } = options;

    return excelData.map((row) => {
        // Transform restTime from [0][0] to [6][1] format to nested array
        const restTime = [];
        for (let i = 0; i < 7; i++) {
            const dayRestTimes = [];
            for (let j = 0; j < 2; j++) {
                const key = `restTime[${i}][${j}]`;
                const timeValue = row[key] || '';
                dayRestTimes.push(formatTimeWithLeadingZero(timeValue));
            }
            if (dayRestTimes.length > 0) {
                restTime.push([dayRestTimes]);
            } else {
                restTime.push([]);
            }
        }

        // Transform workTime from [0][0] and [0][1] to nested array
        const workTime = [];
        if (row['workTime[0][0]'] && row['workTime[0][1]']) {
            workTime.push([
                formatTimeWithLeadingZero(row['workTime[0][0]']),
                formatTimeWithLeadingZero(row['workTime[0][1]']),
            ]);
        }

        // Transform color to object format
        const color = row.color ? { hex: '#' + row.color } : null;

        // Find parentGroupList ID from workGroupLists
        const parentGroupList = [];
        if (row.parentGroupList) {
            const group = findByAnyName(workGroupLists, row.parentGroupList);
            if (group && group.value) {
                parentGroupList.push(group.value);
            }
        }

        // 템플릿종류(어느 언어로 적어도 됨)
        const type = resolveTemplateType(row.type, templete);

        // Find parentId from restTypeLists
        let parentId = null;
        if (row.parentId) {
            if (type === 'WORK_SCHDULE') {
                parentId = null;
            } else {
                const restType = findByAnyName(restTypeLists, row.parentId);
                if (restType && restType.value) {
                    parentId = restType.value;
                }
            }
        }

        // Build the payload
        const payload = {
            ...initPayload,
            companyId,
            restAuto: row.restAuto === 'Y',
            name: row.name,
            code: row.code,
            shortcutKey: row.shortcutKey,
            type,
            color,
            workTime,
            restTime,
            parentGroupList,
            parentId,
        };

        return payload;
    });
};

/**
 * Initial payload template for shift management
 */
export const initPayload = {
  // 공통코드
  id: null,
  // companyId: company.get.id,
  name: '',
  code: '',
  color: {
    hex: '#4DA3F5',
  },
  type: 'WORK_SCHDULE', // _SCHDULE 붙일것
  memo: '', // 설명
  paid: true, // 유급 무급
  deduction: 0, // 차감

  // 근무
  workTime: [['', '']], // 근무시간
  // 여기부터는 추후 구현 기능
  restTime: [[['', '']], [['', '']], [['', '']], [['', '']], [['', '']], [['', '']], [['', '']]], // 휴식지정
  restAuto: false, // 휴식 수동

  // 연차
  paidInfo: { paidType: 'UNPAID', paidUnit: 'd', paidValue: 0 }, // 급여 지급정보
  option: 'REQUEST', // 휴가 부여 방법
  dayOff: false, // 휴일 포함 여부
  gender: '', // 성별
  document: '', // 증명자료 제출
  start: { unit: 'y', value: 0 }, // 입사후 부여 +시점
  end: { unit: 'd', value: 0 }, // 사용 만료 기한
  use: {}, // 분할 사용여부
  onOff: true, // 사용여부

  // 외근
  // 출장
  areaId: null,
  allDay: {
    use: false,
    time: 0,
  }, // 간주근로 여부
  shortcutKey: null, // 단축키
  parentId: null,
  parentGroupList: [],
};

/**
 * Transform Excel data to create pattern payload format
 * @param {Array} excelData - Array of Excel row data
 * @param {Object} options - Options including workGroupLists, templateGroupsList, companyId
 * @returns {Array} - Transformed pattern payload data
 */
export const transformDataToPatternPayload = (excelData, options = {}) => {
  const { workGroupLists = [], templateGroupsList = [], companyId } = options;

  return excelData.map((row) => {
    // Find parentGroupList ID from workGroupLists
    const parentGroupList = [];
    if (row.parentGroupList) {
      const group = findByAnyName(workGroupLists, row.parentGroupList);
      if (group?.value) {
        parentGroupList.push(group.value);
      }
    }

    // Transform patternCycleList - map template names to their IDs
    const patternCycleList = [];
    const cycleTotalDays = row.cycleTotalDays ? Number(row.cycleTotalDays) : 0;

    for (let i = 0; i < cycleTotalDays; i++) {
      const key = `patternCycleList[${i}]`;
      const templateName = row[key];

      if (templateName) {
        const template = templateGroupsList.find((t) => String(t.code) === String(templateName));
        if (template?.value) {
          patternCycleList.push(template.value);
        } else {
          patternCycleList.push(0);
        }
      } else {
        patternCycleList.push(0);
      }
    }

    // Build the payload
    // Format cycleStartDt to Korea/Seoul timezone
    let cycleStartDt = '';
    if (row.cycleStartDt) {
      cycleStartDt = moment.tz(row.cycleStartDt, 'Asia/Seoul').format();
    }

    const payload = {
      companyId,
      patternCycleCode: row.patternCycleCode.trim() || '',
      patternCycleName: row.patternCycleName || '',
      memo: row.memo || '',
      parentGroupList,
      cycleStartDt,
      cycleTotalDays,
      patternCycleList,
    };

    return payload;
  });
};

export const transformDataToPersonalPayload = (excelData, options = {}) => {
  const { companyId, patternCycleList, employList } = options;

  return excelData.map((row) => {
    const patternCycleId =
      patternCycleList.find((p) => String(p.code) === String(row.patternCycleCode))?.value || null;

    // Find userId from employList based on companyNumber
    const employee = employList?.find(
      (emp) => String(emp.companyNumber) === String(row.companyNumber),
    );
    const userId = employee?.userId || null;

    const payload = {
      companyId,
      patternCycleId,
      userId,
      effectStartDt: row.effectStartDt ? moment.tz(row.effectStartDt, 'Asia/Seoul').format() : null,
      effectEndDt: moment.tz(row.effectEndDt || '9999-12-31', 'Asia/Seoul').format(),
    };

    return payload;
  });
};
